<template>
  <div class="autocomplete-wrapper">
    <v-text-field
      ref="inputElement"
      :model-value="value"
      :placeholder="placeholder"
      :label="$t('valuation.labels.address')"
      :name="name"
      variant="outlined"
      density="compact"
      class="mb-4"
      hide-details
      @update:model-value="handleInputChange"
      @keydown="handleKeyDown"
      @focus="onFocus"
    />

    <Teleport to="body">
      <v-card
        v-if="shouldShowSuggestions"
        ref="suggestionsElement"
        class="suggestions py-4"
        :style="{
          top: `${dropdownPosition.top}px`,
          left: `${dropdownPosition.left}px`,
          width: `${dropdownPosition.width}px`,
          position: 'absolute',
          maxHeight: '300px',
          overflowY: 'auto',
          zIndex: 100000,
        }"
        :elevation="4"
      >
        <v-list
          density="compact"
          class="pa-0"
        >
          <v-list-item
            v-for="(suggestion, index) in suggestions"
            :key="index"
            :active="index === selectedSuggestionIndex"
            :class="{ 'bg-grey-lighten-5': index === selectedSuggestionIndex }"
            @click="selectSuggestion(suggestion)"
            @mouseenter="selectedSuggestionIndex = index"
            class="suggestion-item"
          >
            <template v-slot:prepend>
              <div class="d-flex align-center text-medium-emphasis mr-3">
                <mapPin class="map-pin" />
              </div>
            </template>
            <v-list-item-title
              class="text-body-2 text-truncate"
              :title="suggestion.display_name"
            >
              <template
                v-for="(part, index) in getHighlightedParts(formatAddress(suggestion), value)"
                :key="index"
              >
                <span :class="{ 'highlighted-text': part.isMatch }">{{ part.text }}</span>
              </template>
            </v-list-item-title>
          </v-list-item>
        </v-list>
      </v-card>
    </Teleport>
  </div>
</template>

<script setup lang="ts">
import type { ApolloClient } from '@apollo/client/core'
import { DefaultApolloClient } from '@vue/apollo-composable'
import { v4 as uuid } from 'uuid'
import { computed, inject, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'

import GoogleGeocodeQuery from '../queries/GoogleGeocode.gql'
import GooglePlacesQuery from '../queries/GooglePlaces.gql'
// @ts-ignore
import mapPin from './design-elements/mapPin.vue'

// Get the Apollo client instance from the Vue app context
const apolloClient = inject<ApolloClient<any>>(DefaultApolloClient)

// Define the structure for suggestion items
interface SuggestionItem {
  display_name: string
  lat: string
  lon: string
  address?: any
  place_id?: string
}

// Interface for cache entries
interface CacheEntry {
  data: SuggestionItem[]
  timestamp: number
}

// Constants for cache configuration
const CACHE_MAX_SIZE = 50 // Maximum number of entries in the cache
const CACHE_MAX_AGE = 1000 * 60 * 30 // 30 minutes in milliseconds

// Props definition
const props = defineProps({
  modelValue: {
    type: String,
    default: '',
  },
  placeholder: {
    type: String,
    default: '',
  },
  country: {
    type: String,
    default: '',
  },
  name: {
    type: String,
    default: 'search',
  },
  preventSuggestions: {
    type: Boolean,
    default: false,
  },
})

// Emits definition
const emit = defineEmits(['update:modelValue', 'on-input', 'on-change', 'suggestion-selected'])

// Get current locale from i18n
const { locale } = useI18n()

// State variables
const value = ref(props.modelValue)
const inputElement = ref<HTMLInputElement | null>(null)
const suggestions = ref<SuggestionItem[]>([])
const showSuggestions = ref(false)
const selectedSuggestionIndex = ref(-1)
const previousValue = ref(props.modelValue)
const fetchInProgress = ref(false)
const lastFetchTime = ref(0)
const debounceTimer = ref<number | null>(null)
const suggestionsElement = ref<{ $el: HTMLElement } | HTMLElement | null>(null)
const dropdownPosition = ref({ top: 0, left: 0, width: 0 })
const sessionToken = ref<any>(null)
let cacheCleanupInterval: number | null = null

// Session token for Google API
const getOrCreateSessionToken = () => {
  if (sessionToken.value) return sessionToken.value

  // Generate a UUID-v4 as session token
  const token = uuid()
  sessionToken.value = token
  return token
}

// Cache for storing previous search results with timestamps
const resultsCache = ref<Record<string, CacheEntry>>({})

// Function to clean up the cache
const cleanupCache = () => {
  const now = Date.now()
  const cacheEntries = Object.entries(resultsCache.value)

  // Remove expired entries
  const validEntries = cacheEntries.filter(([, entry]) => {
    return now - entry.timestamp < CACHE_MAX_AGE
  })

  // If we're still over the limit, remove oldest entries
  if (validEntries.length > CACHE_MAX_SIZE) {
    // Sort by timestamp (oldest first)
    validEntries.sort((a, b) => a[1].timestamp - b[1].timestamp)
    // Keep only the newest entries
    validEntries.splice(0, validEntries.length - CACHE_MAX_SIZE)
  }

  // Rebuild the cache object
  const newCache: Record<string, CacheEntry> = {}
  validEntries.forEach(([key, entry]) => {
    newCache[key] = entry
  })

  resultsCache.value = newCache
}

// Compute if suggestions should be shown
const shouldShowSuggestions = computed(() => {
  return (
    showSuggestions.value &&
    suggestions.value.length > 0 &&
    value.value.length > 2 &&
    !props.preventSuggestions
  )
})

// Format addresses based on Google Places suggestion
const formatAddress = (suggestion: SuggestionItem) => {
  if (!suggestion.address) {
    return suggestion.display_name
  }
  return suggestion.display_name
}

// Update dropdown position based on input element
const updateDropdownPosition = () => {
  if (!inputElement.value) return

  const rect = inputElement.value.getBoundingClientRect()
  const scrollLeft = window.pageXOffset || document.documentElement.scrollLeft
  const scrollTop = window.pageYOffset || document.documentElement.scrollTop

  dropdownPosition.value = {
    top: rect.bottom + scrollTop,
    left: rect.left + scrollLeft,
    width: rect.width,
  }
}

// Handle focus on input
const onFocus = () => {
  if (value.value.length > 2 && !props.preventSuggestions) {
    // Clean up cache before checking
    cleanupCache()

    // Check cache first
    const cacheKey = `${value.value}:${props.country || ''}`
    if (resultsCache.value[cacheKey] && resultsCache.value[cacheKey].data.length > 0) {
      suggestions.value = resultsCache.value[cacheKey].data
      showSuggestions.value = true
      updateDropdownPosition()
    } else if (suggestions.value.length === 0) {
      // If no cached results, fetch new ones
      fetchSuggestions(value.value)
    }
  }
}

// Handle clicks outside the component
const handleOutsideClick = (e: MouseEvent) => {
  const element = e.target as Node

  // Check if both references exist before using contains
  if (!inputElement.value) {
    return
  }

  // Make sure the suggestions element is a valid DOM node that has the contains method
  const suggestionsNode =
    '$el' in (suggestionsElement.value || {})
      ? (suggestionsElement.value as { $el: HTMLElement }).$el
      : (suggestionsElement.value as HTMLElement)

  if (!suggestionsNode || typeof suggestionsNode.contains !== 'function') {
    // If we can't properly check the suggestion element, just check the input
    if (!inputElement.value.contains(element)) {
      showSuggestions.value = false
    }
    return
  }

  // Check if the click is outside both the input and suggestions
  if (!inputElement.value.contains(element) && !suggestionsNode.contains(element)) {
    showSuggestions.value = false
  }
}

// Handle input changes
const handleInputChange = (inputValue: string) => {
  // Only update if the value has actually changed
  if (inputValue === previousValue.value) {
    return
  }

  value.value = inputValue
  previousValue.value = inputValue

  if (debounceTimer.value !== null) {
    clearTimeout(debounceTimer.value)
  }

  // Check if suggestions should be prevented
  if (props.preventSuggestions) {
    suggestions.value = []
    showSuggestions.value = false
    emit('on-input', { value: inputValue })
    return
  }

  if (inputValue.length > 2) {
    debounceTimer.value = window.setTimeout(() => {
      fetchSuggestions(inputValue)
    }, 500) // Reduced debounce time for better responsiveness
  } else {
    suggestions.value = []
    showSuggestions.value = false
  }

  emit('on-input', { value: inputValue })
}

// Fetch suggestions using GraphQL
const fetchSuggestions = async (query: string) => {
  if (props.preventSuggestions) {
    suggestions.value = []
    showSuggestions.value = false
    return
  }

  if (fetchInProgress.value || query !== value.value || query === '') {
    return
  }

  // Clean up cache before checking
  cleanupCache()

  // Check cache first
  const cacheKey = `${query}:${props.country || ''}`
  if (resultsCache.value[cacheKey] && resultsCache.value[cacheKey].data.length > 0) {
    suggestions.value = resultsCache.value[cacheKey].data
    showSuggestions.value = true
    updateDropdownPosition()
    return
  }

  fetchInProgress.value = true
  lastFetchTime.value = Date.now()
  updateDropdownPosition()

  try {
    if (!apolloClient) {
      throw new Error('Apollo client not found. Cannot fetch suggestions.')
    }

    // Get session token
    const token = getOrCreateSessionToken()

    // Prepare the input object with all required fields
    const placesInput = {
      search: query,
      sessionToken: token,
      types: ['address'],
      language: locale.value,
      ...(props.country ? { country: props.country } : {}),
    }

    // Execute the places query directly
    const { data, errors } = await apolloClient.query({
      query: GooglePlacesQuery,
      variables: { input: placesInput },
    })

    if (errors) {
      console.error('Error fetching suggestions:', errors)
      suggestions.value = []
      showSuggestions.value = false
      fetchInProgress.value = false
      return
    }

    if (data?.getGooglePlaces) {
      const placeSuggestions = data.getGooglePlaces

      // Transform predictions to our format
      const transformedResults: SuggestionItem[] = placeSuggestions.map((prediction: any) => {
        return {
          display_name: prediction.description,
          place_id: prediction.placeId,
          // We'll set these properly when selected
          lat: '',
          lon: '',
          address: {},
        }
      })

      suggestions.value = transformedResults
      showSuggestions.value = transformedResults.length > 0
      selectedSuggestionIndex.value = -1

      // Cache results with timestamp
      resultsCache.value[cacheKey] = {
        data: transformedResults,
        timestamp: Date.now(),
      }

      // Update positions
      if (showSuggestions.value) {
        updateDropdownPosition()
      }
    }

    fetchInProgress.value = false
  } catch (error) {
    console.error('Error fetching suggestions:', error)
    suggestions.value = []
    showSuggestions.value = false
    fetchInProgress.value = false
  }
}

// Handle keyboard navigation
const handleKeyDown = (e: KeyboardEvent) => {
  if (props.preventSuggestions) {
    return
  }

  if (!showSuggestions.value || suggestions.value.length === 0) return

  switch (e.key) {
    case 'ArrowDown':
      e.preventDefault()
      selectedSuggestionIndex.value = Math.min(
        selectedSuggestionIndex.value + 1,
        suggestions.value.length - 1
      )
      scrollToSelectedSuggestion()
      break
    case 'ArrowUp':
      e.preventDefault()
      selectedSuggestionIndex.value = Math.max(selectedSuggestionIndex.value - 1, -1)
      scrollToSelectedSuggestion()
      break
    case 'Enter':
      e.preventDefault()
      if (selectedSuggestionIndex.value >= 0) {
        selectSuggestion(suggestions.value[selectedSuggestionIndex.value])
      } else if (suggestions.value.length > 0) {
        selectSuggestion(suggestions.value[0])
      }
      break
    case 'Escape':
      e.preventDefault()
      showSuggestions.value = false
      break
  }
}

// Scroll to selected suggestion in dropdown
const scrollToSelectedSuggestion = () => {
  if (suggestionsElement.value && selectedSuggestionIndex.value >= 0) {
    const element =
      '$el' in (suggestionsElement.value || {})
        ? (suggestionsElement.value as { $el: HTMLElement }).$el
        : (suggestionsElement.value as HTMLElement)

    const selectedItem = element.querySelector(
      `.suggestion-item:nth-child(${selectedSuggestionIndex.value + 1})`
    ) as HTMLElement

    if (selectedItem) {
      selectedItem.scrollIntoView({ block: 'nearest' })
    }
  }
}

// Select a suggestion from the dropdown using GraphQL
const selectSuggestion = async (suggestion: SuggestionItem) => {
  try {
    if (suggestion.place_id) {
      if (!apolloClient) {
        throw new Error('Apollo client not found. Cannot select suggestion.')
      }

      const token = getOrCreateSessionToken()

      // Prepare the geocode input with all required fields
      const geocodeInput = {
        placeId: suggestion.place_id,
        sessionToken: token,
        fields: ['formatted_address', 'geometry', 'address_components'],
      }

      // Execute the geocode query directly
      const { data, errors } = await apolloClient.query({
        query: GoogleGeocodeQuery,
        variables: { input: geocodeInput },
      })

      if (errors) {
        console.error('Error getting place details:', errors)

        // Fallback to using just the display name
        value.value = suggestion.display_name
        previousValue.value = value.value
        showSuggestions.value = false

        emit('suggestion-selected', suggestion)
        return
      }

      if (data?.getGooglePlaceDetails) {
        const placeDetails = data.getGooglePlaceDetails

        // Update coordinates
        if (placeDetails.coordinates) {
          suggestion.lat = placeDetails.coordinates.lat.toString()
          suggestion.lon = placeDetails.coordinates.lng.toString()
        }

        // Update address
        suggestion.address = {
          road: placeDetails.address?.street || '',
          city: placeDetails.address?.city || '',
          postcode: placeDetails.address?.postcode || '',
          house_number: placeDetails.address?.houseNumber || '',
          country: placeDetails.address?.country || '',
          country_code: placeDetails.address?.countryCode || props.country?.toLowerCase() || '',
        }

        // Create a new session token for the next request
        sessionToken.value = null

        // Update UI
        const formattedAddress = placeDetails.formattedAddress || formatAddress(suggestion)
        value.value = formattedAddress || suggestion.display_name
        previousValue.value = value.value
        showSuggestions.value = false

        emit('suggestion-selected', suggestion)
      }
    } else {
      // If no place_id, just use what we have
      const formattedAddress = formatAddress(suggestion)
      value.value = formattedAddress || suggestion.display_name
      previousValue.value = value.value
      showSuggestions.value = false

      emit('suggestion-selected', suggestion)
    }
  } catch (error) {
    console.error('Error selecting suggestion:', error)

    value.value = suggestion.display_name
    previousValue.value = value.value
    showSuggestions.value = false

    emit('suggestion-selected', suggestion)
  }
}

// Search for a place by address using GraphQL
const getPlaceForAddress = async (address: string): Promise<SuggestionItem | null> => {
  if (!address || address.trim() === '') {
    return null
  }

  try {
    if (!apolloClient) {
      throw new Error('Apollo client not found. Cannot get place for address.')
    }

    const token = getOrCreateSessionToken()

    // Prepare the places input with all required fields
    const placesInput = {
      search: address,
      sessionToken: token,
      types: ['address'],
      language: locale.value,
      ...(props.country ? { country: props.country } : {}),
    }

    // First, directly execute the places query using apolloClient
    const placesResponse = await apolloClient.query({
      query: GooglePlacesQuery,
      variables: { input: placesInput },
    })

    if (!placesResponse.data?.getGooglePlaces?.length) {
      return null
    }

    // Get the first prediction
    const prediction = placesResponse.data.getGooglePlaces[0]

    // Prepare geocode input with all required fields
    const geocodeInput = {
      placeId: prediction.placeId,
      sessionToken: token,
      fields: ['formatted_address', 'geometry', 'address_components'],
    }

    // Execute the geocode query directly
    const geocodeResponse = await apolloClient.query({
      query: GoogleGeocodeQuery,
      variables: { input: geocodeInput },
    })

    if (!geocodeResponse.data?.getGooglePlaceDetails) {
      return null
    }

    const placeDetails = geocodeResponse.data.getGooglePlaceDetails

    // Create a new session token for the next request
    sessionToken.value = null

    return {
      display_name: placeDetails.formattedAddress || prediction.description,
      place_id: prediction.placeId,
      lat: placeDetails.coordinates?.lat.toString() || '',
      lon: placeDetails.coordinates?.lng.toString() || '',
      address: {
        road: placeDetails.address?.street || '',
        city: placeDetails.address?.city || '',
        country: placeDetails.address?.country || '',
        postcode: placeDetails.address?.postcode || '',
        house_number: placeDetails.address?.houseNumber || '',
        country_code: placeDetails.address?.countryCode || props.country?.toLowerCase() || '',
      },
    }
  } catch (error) {
    console.error('Error geocoding address:', error)
    return null
  }
}

// Replace the highlightMatch method with this safer version
const getHighlightedParts = (text: string, query: string) => {
  if (!query || query.length < 2) return [{ text, isMatch: false }]

  // Escape special characters in the query
  const escapedQuery = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

  // Create a case-insensitive regular expression
  const regex = new RegExp(`(${escapedQuery})`, 'gi')

  const parts = []
  let lastIndex = 0
  let match

  while ((match = regex.exec(text)) !== null) {
    // Add text before the match
    if (match.index > lastIndex) {
      parts.push({
        text: text.slice(lastIndex, match.index),
        isMatch: false,
      })
    }

    // Add the matched text
    parts.push({
      text: match[0],
      isMatch: true,
    })

    lastIndex = regex.lastIndex
  }

  // Add any remaining text
  if (lastIndex < text.length) {
    parts.push({
      text: text.slice(lastIndex),
      isMatch: false,
    })
  }

  return parts
}

watch(
  () => props.modelValue,
  (newValue) => {
    if (newValue !== value.value) {
      value.value = newValue
      previousValue.value = newValue
    }
  }
)

watch(
  () => props.preventSuggestions,
  (newValue) => {
    if (newValue) {
      suggestions.value = []
      showSuggestions.value = false
    }
  }
)

watch(value, (newValue) => {
  emit('update:modelValue', newValue)
})

// Watch for changes to shouldShowSuggestions
watch(shouldShowSuggestions, (newValue) => {
  if (newValue) {
    updateDropdownPosition()
    // Add resize event listener to update position when window resizes
    window.addEventListener('resize', updateDropdownPosition)
    // Add scroll event listener to update position when page scrolls
    window.addEventListener('scroll', updateDropdownPosition)
  } else {
    // Remove event listeners when dropdown is hidden
    window.removeEventListener('resize', updateDropdownPosition)
    window.removeEventListener('scroll', updateDropdownPosition)
  }
})

onMounted(() => {
  document.addEventListener('click', handleOutsideClick)
  updateDropdownPosition() // Initial position update

  // Set up periodic cache cleanup
  cacheCleanupInterval = window.setInterval(cleanupCache, CACHE_MAX_AGE / 2)
})

onBeforeUnmount(() => {
  document.removeEventListener('click', handleOutsideClick)
  window.removeEventListener('resize', updateDropdownPosition)
  window.removeEventListener('scroll', updateDropdownPosition)

  if (debounceTimer.value !== null) {
    clearTimeout(debounceTimer.value)
  }

  // Clear the cache cleanup interval
  if (cacheCleanupInterval !== null) {
    clearInterval(cacheCleanupInterval)
  }
})

defineExpose({
  getPlaceForAddress,
})
</script>

<style scoped>
.autocomplete-wrapper {
  position: relative;
  width: 100%;
}

.suggestions {
  border-radius: unset !important;
}

.suggestion-item {
  transition: background-color 0.2s;
}

.highlighted-text {
  font-weight: bold;
}

.map-pin {
  height: 28px;
}
</style>
